/**
 * Fundos virtuais / blur na câmera local (Twilio Video Processors).
 * Assets em /twilio-video-processors/ (sync via scripts/sync-twilio-video-processors-assets.mjs).
 */

import { isScreenShareTrack } from "./videoScreenShare";

export const VIDEO_BG_ASSETS_PATH = "/twilio-video-processors/";

/** Fundo alinhado ao tema MUI escolhido pelo usuário. */
export const THEME_VIDEO_BACKGROUND_ID = "theme";

/** Fundo padrão: logo Xvision no canto superior esquerdo. */
export const DEFAULT_VIDEO_BACKGROUND_ID = "xpro";
export const DEFAULT_VIDEO_BACKGROUND_MODE = `preset:${DEFAULT_VIDEO_BACKGROUND_ID}`;

function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex) {
  const raw = String(hex || "").replace("#", "").trim();
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return { r, g, b };
  }
  if (raw.length !== 6) return { r: 25, g: 118, b: 210 };
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const to = (n) => Math.round(clamp01(n / 255) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Escurece (amount < 0) ou clareia (amount > 0) um hex. */
export function shadeHex(hex, amount = 0) {
  const { r, g, b } = hexToRgb(hex);
  const t = amount < 0 ? 0 : 255;
  const p = Math.abs(amount);
  return rgbToHex({
    r: r + (t - r) * p,
    g: g + (t - g) * p,
    b: b + (t - b) * p,
  });
}

/**
 * Gradiente de fundo a partir do tema MUI (cores mais profundas para a pessoa destacar).
 */
export function colorsFromMuiTheme(theme) {
  const primary = theme?.palette?.primary || {};
  const main = primary.main || "#1976d2";
  const dark = primary.dark || shadeHex(main, -0.35);
  const light = primary.light || shadeHex(main, 0.22);
  const mode = theme?.palette?.mode || "light";
  if (mode === "dark") {
    return [shadeHex(dark, -0.5), shadeHex(dark, -0.15), main];
  }
  return [shadeHex(main, -0.62), shadeHex(main, -0.28), shadeHex(light, -0.05)];
}

/**
 * Preset dinâmico alinhado ao tema atual do usuário.
 */
export function buildThemeVideoBackgroundPreset(theme, { themeKey = "", themeLabel = "" } = {}) {
  const colors = colorsFromMuiTheme(theme);
  const accent = theme?.palette?.secondary?.main || shadeHex(colors[2] || "#1976d2", 0.15);
  const label = themeLabel
    ? `Tema · ${themeLabel}`
    : themeKey
      ? `Tema · ${themeKey}`
      : "Seu tema";
  return {
    id: THEME_VIDEO_BACKGROUND_ID,
    label,
    kind: "theme",
    themeKey: themeKey || null,
    colors,
    accent,
  };
}

/** @typedef {'none' | 'blur' | `preset:${string}` | 'custom'} VideoBackgroundMode */

export const VIDEO_BACKGROUND_PRESETS = [
  {
    id: DEFAULT_VIDEO_BACKGROUND_ID,
    label: "Xvision",
    kind: "logo",
    colors: ["#071525", "#0b3d5c", "#1565a8"],
    logoSrc: "/brands/xpro/xvision-logo.png",
    accent: "#ff6a00",
    logoPosition: "top-left",
  },
  {
    id: "office",
    label: "Escritório",
    kind: "gradient",
    colors: ["#1e3a5f", "#2d5a87", "#4a7ba7"],
  },
  {
    id: "soft",
    label: "Suave",
    kind: "gradient",
    colors: ["#e8eaf6", "#c5cae9", "#9fa8da"],
  },
  {
    id: "warm",
    label: "Quente",
    kind: "gradient",
    colors: ["#3e2723", "#5d4037", "#8d6e63"],
  },
  {
    id: "forest",
    label: "Verde",
    kind: "gradient",
    colors: ["#1b5e20", "#2e7d32", "#66bb6a"],
  },
  {
    id: "night",
    label: "Noite",
    kind: "gradient",
    colors: ["#0d1b2a", "#1b263b", "#415a77"],
  },
];

/**
 * Preview local usa scaleX(-1) (espelho). Fundos com texto/logo não podem
 * ser espelhados — a marca ficaria invertida. Remotes nunca usam o espelho.
 */
export function shouldMirrorLocalPreview(mode) {
  const m = String(mode || "");
  if (!m.startsWith("preset:")) return true;
  const id = m.slice("preset:".length);
  const preset = VIDEO_BACKGROUND_PRESETS.find((p) => p.id === id);
  return preset?.kind !== "logo";
}

/** Dimensões reais da track de vídeo (evita crop do ImageFit.Cover). */
export function dimensionsFromVideoTracks(tracks = []) {
  const list = Array.isArray(tracks) ? tracks : [tracks];
  for (const t of list) {
    if (!t || t.kind !== "video") continue;
    try {
      const s = t?.mediaStreamTrack?.getSettings?.() || {};
      const w = Number(s.width);
      const h = Number(s.height);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 16 && h > 16) {
        return { width: Math.round(w), height: Math.round(h) };
      }
    } catch {
      /* ignore */
    }
  }
  return { width: 1280, height: 720 };
}

/**
 * Cover corta bordas quando a proporção da câmera ≠ da imagem.
 * Logo/tema: Fill para a marca inteira caber no frame publicado.
 */
export function backgroundFitForPreset(preset) {
  if (preset?.kind === "logo" || preset?.kind === "theme") return "Fill";
  return "Cover";
}
const PROCESSOR_OPTS = {
  inputFrameBufferType: "videoframe",
  outputFrameBufferContextType: "bitmaprenderer",
};

let processorsModulePromise = null;
const logoBackgroundCache = new Map();

async function loadProcessors() {
  if (!processorsModulePromise) {
    processorsModulePromise = import("@twilio/video-processors").catch((err) => {
      processorsModulePromise = null;
      throw err;
    });
  }
  return processorsModulePromise;
}

export async function isVideoBackgroundSupported() {
  try {
    const { isSupported } = await loadProcessors();
    return Boolean(isSupported);
  } catch {
    return false;
  }
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Falha ao carregar imagem de fundo."));
    img.src = src;
  });
}

/** Remove fundo preto da logo (PNG com bg preto) antes de desenhar no canvas. */
function drawLogoKnockingOutBlack(ctx, logo, dx, dy, dw, dh) {
  const tmp = document.createElement("canvas");
  tmp.width = Math.max(1, logo.naturalWidth || logo.width || 1);
  tmp.height = Math.max(1, logo.naturalHeight || logo.height || 1);
  const tctx = tmp.getContext("2d", { willReadFrequently: true });
  if (!tctx) {
    ctx.drawImage(logo, dx, dy, dw, dh);
    return;
  }
  tctx.drawImage(logo, 0, 0);
  try {
    const imageData = tctx.getImageData(0, 0, tmp.width, tmp.height);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] < 30 && d[i + 1] < 30 && d[i + 2] < 30) {
        d[i + 3] = 0;
      }
    }
    tctx.putImageData(imageData, 0, 0);
  } catch {
    /* CORS / tainted canvas — desenha original */
  }
  ctx.drawImage(tmp, dx, dy, dw, dh);
}

export async function createGradientBackgroundImage(colors, width = 1280, height = 720) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponível.");
  const stops = Array.isArray(colors) && colors.length ? colors : ["#202124", "#3c4043"];
  const g = ctx.createLinearGradient(0, 0, width, height);
  stops.forEach((c, i) => {
    g.addColorStop(stops.length === 1 ? 0 : i / (stops.length - 1), c);
  });
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);
  // Vinheta leve (estilo Meet)
  const vg = ctx.createRadialGradient(
    width * 0.5,
    height * 0.45,
    Math.min(width, height) * 0.15,
    width * 0.5,
    height * 0.5,
    Math.max(width, height) * 0.7
  );
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0,0.28)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, width, height);
  return loadHtmlImage(canvas.toDataURL("image/jpeg", 0.9));
}

/**
 * Fundo padrão com logo (gradiente escuro + logo no canto superior esquerdo).
 */
export async function createLogoBackgroundImage({
  logoSrc = "/brands/xpro/xvision-logo.png",
  colors = ["#071525", "#0b3d5c", "#1565a8"],
  accent = "#ff6a00",
  logoPosition = "top-left",
  width = 1280,
  height = 720,
} = {}) {
  const cacheKey = `v2|${logoSrc}|${colors.join(",")}|${accent}|${logoPosition}|${width}x${height}`;
  if (logoBackgroundCache.has(cacheKey)) {
    return logoBackgroundCache.get(cacheKey);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponível.");

  const stops = Array.isArray(colors) && colors.length ? colors : ["#071525", "#0b3d5c"];
  const g = ctx.createLinearGradient(0, 0, width, height);
  stops.forEach((c, i) => {
    g.addColorStop(stops.length === 1 ? 0 : i / (stops.length - 1), c);
  });
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);

  // Acento laranja discreto (canto inferior)
  const accentGrad = ctx.createRadialGradient(
    width * 0.88,
    height * 0.88,
    10,
    width * 0.88,
    height * 0.88,
    width * 0.4
  );
  accentGrad.addColorStop(0, `${accent}28`);
  accentGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = accentGrad;
  ctx.fillRect(0, 0, width, height);

  try {
    const logo = await loadHtmlImage(logoSrc);
    // Margem maior: Cover/object-fit e aspect da câmera costumam “comer” as bordas.
    const padX = Math.round(width * 0.055);
    const padY = Math.round(height * 0.09);
    const maxW = width * 0.3;
    const maxH = height * 0.14;
    const scale = Math.min(maxW / logo.naturalWidth, maxH / logo.naturalHeight);
    const lw = Math.max(1, logo.naturalWidth * scale);
    const lh = Math.max(1, logo.naturalHeight * scale);

    let lx = padX;
    let ly = padY;
    if (logoPosition === "center") {
      lx = (width - lw) / 2;
      ly = (height - lh) / 2;
    } else if (logoPosition === "top-right") {
      lx = width - padX - lw;
      ly = padY;
    }

    // Halo leve atrás da logo (canto)
    const hx = lx + lw * 0.35;
    const hy = ly + lh * 0.5;
    const halo = ctx.createRadialGradient(hx, hy, 8, hx, hy, Math.max(lw, lh) * 1.6);
    halo.addColorStop(0, "rgba(255,255,255,0.12)");
    halo.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, width, height);

    ctx.globalAlpha = 0.98;
    drawLogoKnockingOutBlack(ctx, logo, lx, ly, lw, lh);
    ctx.globalAlpha = 1;
  } catch {
    // Sem logo: mantém só o gradiente
  }

  // Vinheta
  const vg = ctx.createRadialGradient(
    width * 0.5,
    height * 0.5,
    Math.min(width, height) * 0.22,
    width * 0.5,
    height * 0.5,
    Math.max(width, height) * 0.72
  );
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0,0.32)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, width, height);

  const img = await loadHtmlImage(canvas.toDataURL("image/jpeg", 0.92));
  logoBackgroundCache.set(cacheKey, img);
  return img;
}

export async function resolvePresetBackgroundImage(preset, { logoSrc, width, height } = {}) {
  if (!preset) throw new Error("Fundo pré-definido não encontrado.");
  const w = width > 0 ? Math.round(width) : 1280;
  const h = height > 0 ? Math.round(height) : 720;
  if (preset.kind === "logo") {
    return createLogoBackgroundImage({
      // Preset com logo própria (ex.: Xvision) tem prioridade sobre logo do brand.
      logoSrc: preset.logoSrc || logoSrc || "/brands/xpro/xvision-logo.png",
      colors: preset.colors,
      accent: preset.accent,
      logoPosition: preset.logoPosition || "top-left",
      width: w,
      height: h,
    });
  }
  // kind "theme" | "gradient" — ambos usam gradiente
  return createGradientBackgroundImage(preset.colors, w, h);
}

export async function loadImageFromFile(file) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Selecione uma imagem (JPG, PNG ou WebP).");
  }
  const url = URL.createObjectURL(file);
  try {
    return await loadHtmlImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function getLocalVideoTracks(trackLists = []) {
  const out = [];
  const seen = new Set();
  for (const list of trackLists) {
    for (const t of list || []) {
      if (t?.kind === "video" && !isScreenShareTrack(t) && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  return out;
}

function removeProcessorsFromTrack(track) {
  if (!track || typeof track.processor === "undefined") return;
  try {
    const current = track.processor;
    if (current) track.removeProcessor(current);
  } catch {
    /* ignore */
  }
}

/**
 * Aplica efeito de fundo em uma ou mais LocalVideoTracks.
 * @returns {{ mode: VideoBackgroundMode, processor: object|null }}
 */
export async function applyVideoBackground({
  mode = "none",
  tracks = [],
  blurRadius = 15,
  backgroundImage = null,
  assetsPath = VIDEO_BG_ASSETS_PATH,
  fitType = "Cover",
} = {}) {
  const videoTracks = getLocalVideoTracks([tracks]);
  if (!videoTracks.length) {
    return { mode: "none", processor: null };
  }

  for (const t of videoTracks) removeProcessorsFromTrack(t);

  if (!mode || mode === "none") {
    return { mode: "none", processor: null };
  }

  const supported = await isVideoBackgroundSupported();
  if (!supported) {
    const err = new Error(
      "Efeitos de fundo não são suportados neste navegador ou dispositivo."
    );
    err.code = "video.background_unsupported";
    throw err;
  }

  const {
    GaussianBlurBackgroundProcessor,
    VirtualBackgroundProcessor,
    ImageFit,
  } = await loadProcessors();

  let processor = null;

  if (mode === "blur") {
    processor = new GaussianBlurBackgroundProcessor({
      assetsPath,
      blurFilterRadius: blurRadius,
    });
  } else {
    if (!backgroundImage) {
      throw new Error("Imagem de fundo obrigatória.");
    }
    const fitKey = String(fitType || "Cover");
    const resolvedFit = ImageFit?.[fitKey] || fitKey || ImageFit?.Cover || "Cover";
    processor = new VirtualBackgroundProcessor({
      assetsPath,
      backgroundImage,
      fitType: resolvedFit,
      maskBlurRadius: 5,
    });
  }

  await processor.loadModel();

  for (const t of videoTracks) {
    try {
      t.addProcessor(processor, PROCESSOR_OPTS);
    } catch (e) {
      // Fallback para SDKs mais antigos sem options
      try {
        t.addProcessor(processor);
      } catch (e2) {
        throw e2 || e;
      }
    }
  }

  return { mode, processor };
}

export function clearVideoBackground(tracks = []) {
  for (const t of getLocalVideoTracks([tracks])) {
    removeProcessorsFromTrack(t);
  }
}

/**
 * Canvas de saída do Video Processor (Twilio).
 * `track.attach()` pré-visualiza via `canvas.captureStream()`. No documento que
 * está compartilhando a tela, o Chrome silencia/substitui esse stream (anti
 * hall-of-mirrors) e o usuário local vê a câmera crua — remotes seguem no
 * processedTrack. Mostrar o canvas no DOM evita essa troca.
 */
export function getProcessorOutputCanvas(track) {
  if (!track?.processor) return null;
  const canvas = track._outputFrame;
  return typeof HTMLCanvasElement !== "undefined" && canvas instanceof HTMLCanvasElement
    ? canvas
    : null;
}

function styleLocalPreviewEl(el, { mirror = false, contain = false } = {}) {
  if (!el) return;
  el.style.width = "100%";
  el.style.height = "100%";
  el.style.objectFit = contain ? "contain" : "cover";
  el.style.background = contain ? "#000" : "";
  el.style.transform = mirror && !contain ? "scaleX(-1)" : "";
  el.style.display = "block";
}

function removePreviewCanvases(container) {
  if (!container?.querySelectorAll) return;
  container.querySelectorAll("canvas[data-video-bg-preview]").forEach((el) => {
    el.remove();
  });
}

/**
 * Copia o canvas do processor para um canvas de outro documento (Document PiP).
 * `captureStream()` não atravessa a janela flutuante — o preview local fica cru.
 * O rAF roda no `rafWindow` (a janela do PiP) para não ser throttleado com a aba oculta.
 */
export function startProcessorPreviewCopy(track, destCanvas, { rafWindow = null, onFrame = null } = {}) {
  const src = getProcessorOutputCanvas(track);
  if (!src || !destCanvas) return null;
  const ctx = destCanvas.getContext("2d");
  if (!ctx) return null;
  const host = rafWindow || destCanvas.ownerDocument?.defaultView || (typeof window !== "undefined" ? window : null);
  if (!host?.requestAnimationFrame) return null;
  let raf = 0;
  let stopped = false;
  const draw = () => {
    if (stopped) return;
    const w = Number(src.width) || 0;
    const h = Number(src.height) || 0;
    if (w > 0 && h > 0) {
      if (destCanvas.width !== w) destCanvas.width = w;
      if (destCanvas.height !== h) destCanvas.height = h;
      try {
        ctx.drawImage(src, 0, 0, w, h);
        onFrame?.();
      } catch {
        /* bitmaprenderer / cross-document — próximo frame tenta de novo */
      }
    }
    raf = host.requestAnimationFrame(draw);
  };
  raf = host.requestAnimationFrame(draw);
  return () => {
    stopped = true;
    if (raf) {
      try {
        host.cancelAnimationFrame(raf);
      } catch {
        /* ignore */
      }
    }
  };
}

/**
 * Anexa a câmera local: mantém o `<video>` do attach() (o processor precisa
 * dele no pré-join) e sobrepõe o canvas processado quando existir.
 */
export function attachLocalCameraPreview(track, container, { mirror = false, contain = false } = {}) {
  if (!track || !container) return null;
  try {
    track.detach().forEach((el) => el.remove());
  } catch {
    /* ignore */
  }
  if (container.querySelectorAll) {
    container.querySelectorAll("video, audio").forEach((el) => {
      try {
        el.srcObject = null;
      } catch {
        /* ignore */
      }
      el.remove();
    });
  }
  removePreviewCanvases(container);

  const videoEl = track.attach();
  styleLocalPreviewEl(videoEl, { mirror, contain });
  if (videoEl.tagName === "VIDEO" || videoEl.tagName === "AUDIO") {
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.setAttribute("playsinline", "true");
    const play = videoEl.play?.();
    if (play && typeof play.catch === "function") play.catch(() => {});
  }
  container.appendChild(videoEl);

  const canvas = getProcessorOutputCanvas(track);
  if (canvas) {
    canvas.setAttribute("data-video-bg-preview", "1");
    styleLocalPreviewEl(canvas, { mirror, contain });
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.zIndex = "0";
    canvas.style.pointerEvents = "none";
    videoEl.style.opacity = "0";
    videoEl.style.position = "absolute";
    videoEl.style.inset = "0";
    videoEl.style.pointerEvents = "none";
    videoEl.setAttribute("aria-hidden", "true");
    if (canvas.parentElement !== container) {
      container.appendChild(canvas);
    }
  }

  return videoEl;
}
